"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { isAuthenticated, checkAuthOnLoad, getLoginPath } from "@/lib/auth"

interface AuthGuardProps {
  children: React.ReactNode
  redirectTo?: string
  loadingComponent?: React.ReactNode
}

export function AuthGuard({ 
  children, 
  redirectTo,
  loadingComponent 
}: AuthGuardProps) {
  const resolvedRedirectTo = redirectTo ?? (typeof window !== "undefined" ? getLoginPath() : "/login")
  const [isChecking, setIsChecking] = useState(true)
  const [isAuthed, setIsAuthed] = useState(false)
  const router = useRouter()

  useEffect(() => {
    const checkAuth = () => {
      try {
        const userInfo = checkAuthOnLoad()
        setIsAuthed(userInfo.isAuthenticated)
        
        if (!userInfo.isAuthenticated) {
          console.log("Пользователь не авторизован, перенаправление на", resolvedRedirectTo)
          router.push(resolvedRedirectTo)
        }
      } catch (error) {
        console.error("Ошибка при проверке авторизации:", error)
        setIsAuthed(false)
        router.push(resolvedRedirectTo)
      } finally {
        setIsChecking(false)
      }
    }

    checkAuth()
  }, [router, redirectTo])

  // Показываем загрузку пока проверяем авторизацию
  if (isChecking) {
    if (loadingComponent) {
      return <>{loadingComponent}</>
    }
    
    return (
      <div className="flex items-center justify-center min-h-screen-safe">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-gray-600">Проверка авторизации...</p>
        </div>
      </div>
    )
  }

  // Если не авторизован, ничего не показываем (идет редирект)
  if (!isAuthed) {
    return null
  }

  // Показываем защищенный контент
  return <>{children}</>
}

// HOC для защиты страниц
export function withAuthGuard<P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    redirectTo?: string
    loadingComponent?: React.ReactNode
  }
) {
  const AuthGuardedComponent = (props: P) => {
    return (
      <AuthGuard 
        redirectTo={options?.redirectTo}
        loadingComponent={options?.loadingComponent}
      >
        <Component {...props} />
      </AuthGuard>
    )
  }

  AuthGuardedComponent.displayName = `withAuthGuard(${Component.displayName || Component.name})`
  
  return AuthGuardedComponent
} 